import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useUIStore, useChatStore, useChatInputStore, useModelStore, useAgentStore, useAuthStore, useFileStore, useToastStore, useCoworkerStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import useWorkKeyboardShortcuts from '@/hooks/useWorkKeyboardShortcuts';
import useRequireAuth from '@/hooks/useRequireAuth';
import ChatInputFixed from '@/components/ChatInput/ChatInputFixed';
import ChatInputFloating from '@/components/ChatInput/ChatInputFloating';
import ChatInputFloatingBasic from '@/components/ChatInput/ChatInputFloatingBasic';
import ChatInputDetailed from '@/components/ChatInput/ChatInputDetailed';
import { CHAT_INPUT_STYLE, CONTEXT_NOTIFICATION_LIMITS, MAX_FILES_UPLOADED_IN_MESSAGE } from '@/constants/config';
import { tokenCounter } from '@/constants/utils/tokens';
import { maybeOpenUploadedFileInEditor } from '@/constants/utils/files';
import { uploadFilesWithPlaceholders } from '@/lib/uploadFileWithPlaceholder';
import { isFileProcessing } from '@/lib/pollFileProcessing';
import { lightTap } from '@/constants/utils/haptics';
import useTranslation, { getTranslation } from '@/hooks/useTranslation';
import { FILE_COMPONENT_TYPES, isInlineParam } from '@/components/ChatInput/fileParamUtils';
import { getComposerPerformableAction, isDraftComposerConversationId } from '@/components/ChatInput/composerActionSlot';
import { resolveWorkComposerSender } from '@/constants/work_followup_queue';
import { readWebComposerDomText } from '@/constants/utils/composer_dom_text';
import GoalCommandSuggestions from './GoalCommandSuggestions';
import useGoalCommand from './useGoalCommand';
import { blockIfWorkModeDisabled } from '@/lib/workModeAvailability';
import WorkGoalBar from '@/components/Coworker/WorkGoalBar';
import WorkGoalEditor from '@/components/Coworker/WorkGoalEditor';
import WorkComposerProjectBar from './WorkComposerProjectBar';

// DEBUG logging controls
const DEBUG_CREDITS_LOGS = false;
const debugLog = (...args) => {
  if (!DEBUG_CREDITS_LOGS) return;
  try {
    // Timestamped, component-scoped logs
    console.log('[ChatInput]', new Date().toISOString(), ...args);
  } catch (e) {
    console.log('[ChatInput]', ...args);
  }
};
let DEBUG_CREDITS_SEQ = 0;

// Uploads start as placeholders; completion replaces them in place. Recalculate
// again on replacement/extraction so a skipped, still-uploading preview resumes.
const FILE_CREDIT_EVENTS = [
  'addFile',
  'addFileById',
  'addFilesToContext',
  'removeFile',
  'removeFileById',
  'removeFileByIdFromAllContexts',
  'replaceFileByIdInAllContexts',
  'updateFileById',
  'setFiles',
  'setEditFileIds',
  'initializeNewChatWithFiles',
];

const CREDIT_CALCULATION_EVENTS = [
  'setText',
  ...FILE_CREDIT_EVENTS,
  'setSelectedModels',
  'setSelectedAgents',
  'setParameter',
  // After a clearInput, we want to recalculate credits for the new, cleared state
  'clearInput',
];

const MODEL_AGENT_SELECTION_EVENTS = [
  'setSelectedModels',
  'setSelectedAgents',
  'clearInput'
];

// Constants for debouncing
const TEXT_DEBOUNCE_DELAY = 1000; // 1 second for text changes
const IMMEDIATE_EVENTS = [
  ...FILE_CREDIT_EVENTS,
  'setSelectedModels',
  'setSelectedAgents',
  // Treat clearInput as an immediate event so credits are recalculated right away
  'clearInput',
];
const IMMEDIATE_DEBOUNCE_DELAY = 250; // debounce for non-text events

// Zustand's React 19 adapter requires selector fallbacks to be referentially
// stable. Inline `[]` / `{}` defaults create a different snapshot every time
// the store is read and can put a brand-new composer into a render loop.
const EMPTY_INPUT_LIST = Object.freeze([]);
const EMPTY_INPUT_PARAMETERS = Object.freeze({});

const handleError = (error, context) => {
  console.error(`Error in ChatInput (${context}):`, error);
  // Could add error reporting service here
};

// ============================================================================
// Utility Functions
// ============================================================================

const createChatSelector = (id) => (state) => state.chats.find(c => c.id === id);

// ============================================================================
// Custom Hooks
// ============================================================================

const useCreditCalculation = ({ text, files, selectedModelIds, selectedAgentIds, conversationId, collectionId, calculateMessageCredits, resetCreditCalculation, getFormattedMessage, userId }) => {
  const timerRef = useRef(null);
  const pendingSnapshotRef = useRef(null);
  const lastCalculationRef = useRef({
    text: '',
    files: [],
    selectedModelIds: [],
    selectedAgentIds: []
  });

  // Single unified scheduler. Only one credit calculation can be pending at
  // a time — any new event (text, files, models, agents, params) clears the
  // outstanding timer and replaces it. This collapses what used to be two
  // independent debounce timers (immediate vs. text) that could both fire
  // within a few hundred ms of each other and produce near-duplicate server
  // round-trips for /json/conversations/calculate-credits.
  const scheduleCalculation = useCallback((stateSnapshot, delay, reason) => {
    const seq = ++DEBUG_CREDITS_SEQ;
    pendingSnapshotRef.current = stateSnapshot;
    if (timerRef.current) {
      debugLog(`#${seq} clearing previous debounce timer`, { reason });
      clearTimeout(timerRef.current);
    }
    debugLog(`#${seq} scheduling debounced calculation in ${delay}ms`, { reason });
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      const snapshot = pendingSnapshotRef.current;
      try {
        // Check if any files are still uploading or processing before making the API call
        const currentState = useChatInputStore.getState();
        const currentFiles = currentState.inputStates?.[conversationId || 'new']?.files || [];
        const hasUploadingFiles = currentFiles.some(file => file?.isUploading === true);
        const hasProcessingFiles = currentFiles.some(file => {
          if (!file || file.isUploading) return false;
          const status = file.processingStatus?.status;
          return status === 'pending' || status === 'processing';
        });
        if (hasUploadingFiles || hasProcessingFiles) {
          debugLog(`#${seq} skipping credit calculation - files still uploading or processing`);
          return;
        }

        const formattedMessage = getFormattedMessage(conversationId || 'new');
        await calculateMessageCredits({
          conversationId,
          collectionId,
          userId,
          formattedMessage
        });
        lastCalculationRef.current = snapshot;
        debugLog(`#${seq} debounced calculation success`, { reason });
      } catch (error) {
        handleError(error, 'credit calculation');
        debugLog(`#${seq} debounced calculation error`, error?.message || error);
        resetCreditCalculation();
      }
    }, delay);
  }, [calculateMessageCredits, resetCreditCalculation, conversationId, collectionId, getFormattedMessage, userId]);

  const calculateCredits = useCallback(async (currentState, eventType, eventData) => {
    const seq = ++DEBUG_CREDITS_SEQ;
    const { text, selectedModelIds, selectedAgentIds, files } = currentState;
    const isPlus = useAuthStore.getState().user?.flags?.isPlus;

    // console.log('---calculateCredits', currentState, eventType, eventData);

    debugLog(`#${seq} calculateCredits invoked`, {
      eventType,
      eventData,
      textLength: text?.length || 0,
      filesCount: Array.isArray(files) ? files.length : 0,
      selectedModelIds,
      selectedAgentIds,
      conversationId
    });
    
    if (!isPlus) {
      debugLog(`#${seq} skip: user is not Plus → resetCreditCalculation()`);
      resetCreditCalculation();
      return;
    }

    // Snapshot current values for change detection and post-success tracking
    const stateSnapshot = { text, files, selectedModelIds, selectedAgentIds };

    // For immediate events (files, models, agents), calculate immediately
    if (IMMEDIATE_EVENTS.includes(eventType)) {
      debugLog(`#${seq} immediate calculation scheduled (debounced) for`, eventType);
      scheduleCalculation(stateSnapshot, IMMEDIATE_DEBOUNCE_DELAY, eventType);
      return;
    }

    // Force calculation on specific parameter change (tools), regardless of other guards
    if (eventType === 'setParameter' && eventData?.paramId === 'tools') {
      debugLog(`#${seq} forced calculation scheduled for setParameter:tools`);
      scheduleCalculation(stateSnapshot, IMMEDIATE_DEBOUNCE_DELAY, 'setParameter:tools');
      return;
    }

    // Force calculation on specific parameter change (any parameter with credit config)
    if (eventType === 'setParameter' && eventData?.paramId) {
      try {
        const paramId = eventData.paramId;
        const parameters = Array.isArray(currentState?.parameters) ? currentState.parameters : [];
        const parameterDef = parameters.find(p => p && (p.id === paramId || p.name === paramId));
        const options = parameterDef?.validation?.options || [];
        const selectedValue = (currentState?.selectedParameters && currentState.selectedParameters[paramId] !== undefined)
          ? currentState.selectedParameters[paramId]
          : eventData?.value;
        const selectedOption = options.find(opt => opt && opt.value === selectedValue);
        const hasOptionBaseCost = selectedOption?.credits?.baseCost !== undefined && selectedOption?.credits?.baseCost !== null;
        const hasOptionFormula = !!selectedOption?.credits?.formula;
        const hasParamFormula = !!parameterDef?.credits?.formula;
        const hasParamBaseCost = parameterDef?.credits?.baseCost !== undefined && parameterDef?.credits?.baseCost !== null && parameterDef?.credits?.baseCost !== 0;
        const hasPerUnitCost = !!parameterDef?.credits?.perUnitCost;
        const hasCreditConfig = hasOptionBaseCost || hasOptionFormula || hasParamFormula || hasParamBaseCost || hasPerUnitCost;
        if (hasCreditConfig) {
          debugLog(`#${seq} forced calculation scheduled for setParameter:${paramId} due to credit config`);
          scheduleCalculation(stateSnapshot, IMMEDIATE_DEBOUNCE_DELAY, `setParameter:${paramId}`);
          return;
        }
      } catch (error) {
        handleError(error, 'credit calculation (param option pre-check)');
      }
    }

    // Change detection: skip when the credit-relevant snapshot (text, files,
    // models, agents) is identical to the last one we successfully calculated
    // for. This was previously commented out, which meant every debounced text
    // event fired a server round-trip even when nothing affecting cost had
    // actually changed since the last successful calculation.
    const hasChanged = JSON.stringify(stateSnapshot) !== JSON.stringify(lastCalculationRef.current);
    debugLog(`#${seq} change check`, { hasChanged });
    if (!hasChanged) {
      debugLog(`#${seq} skip: no changes detected`);
      return;
    }

    // For text changes, debounce the calculation (longer delay than
    // immediate events since typing is continuous). scheduleCalculation
    // handles clearing any prior pending timer.
    if (eventType === 'setText') {
      scheduleCalculation(stateSnapshot, TEXT_DEBOUNCE_DELAY, eventType);
    }
  }, [scheduleCalculation, resetCreditCalculation, conversationId]);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      debugLog('cleanup: clearing pending debounce timer');
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return { calculateCredits, cleanup };
};

const usePlaceholderManagement = (setInputPlaceholder, instrumentalOnlyMusic) => {
  return useCallback((modelIds = [], agentIds = []) => {
    try {
      const models = useModelStore.getState().models;
      const agents = useAgentStore.getState().agents;

      const selectedModels = modelIds.map(id => models[id]).filter(Boolean);
      const selectedAgents = agentIds.map(id => agents[id]).filter(Boolean);

      const agentCount = selectedAgents.length;
      const modelCount = selectedModels.length;

      let placeholder = getTranslation('chatInput.placeholderDefault');

      if (agentCount === 1) {
        const agent = selectedAgents[0];
        if (modelCount === 0) {
          placeholder = agent.outputTypes?.includes('image') ? 
            getTranslation('chatInput.placeholderAgentGenerate').replace('%NAME%', agent.name) : 
            getTranslation('chatInput.placeholderAgentSend').replace('%NAME%', agent.name);
        } else if (modelCount === 1) {
          placeholder = getTranslation('chatInput.placeholderAgentWithModel').replace('%AGENT%', agent.name).replace('%MODEL%', selectedModels[0].name);
        } else {
          placeholder = getTranslation('chatInput.placeholderAgentWithModels').replace('%NAME%', agent.name);
        }
      } else if (agentCount === 0 && modelCount > 0) {
        if (modelCount === 1) {
          // Per-model `inputPlaceholder` lets us use copy that matches what
          // the model will actually do with the user's text — e.g. music
          // generators treat the message as song lyrics, so we surface
          // "Write the lyrics for your song…" instead of a generic
          // "Send message to MiniMax Music 2.6". Falls back to the
          // localized template when no override is set.
          // When the model's instrumental toggle is on, lyrics are unused
          // (the track is driven by the description param), so say the box
          // is optional instead of demanding lyrics.
          const customPlaceholder = selectedModels[0]?.inputPlaceholder;
          // Image-only generators can't answer in text. Say so up front, so a
          // user who hand-picks GPT Image and types a question isn't left
          // wondering why they got a picture (or nothing) instead of a reply.
          const output = selectedModels[0]?.capabilities?.output || {};
          const isImageOnlyModel = !!output.hasImages && !output.hasText && !output.hasVideo && !output.has3D;
          placeholder = instrumentalOnlyMusic
            ? getTranslation('chatInput.placeholderInstrumentalMusic')
            : (typeof customPlaceholder === 'string' && customPlaceholder.trim())
              ? customPlaceholder.trim()
              : isImageOnlyModel
                ? getTranslation('chatInput.placeholderImageModel').replace('%NAME%', selectedModels[0].name)
                : getTranslation('chatInput.placeholderModel').replace('%NAME%', selectedModels[0].name);
        } else {
          placeholder = getTranslation('chatInput.placeholderSendToModels');
        }
      }

      setInputPlaceholder(null, placeholder);
    } catch (error) {
      handleError(error, 'placeholder update');
      setInputPlaceholder(null, getTranslation('chatInput.placeholderDefault'));
    }
  }, [setInputPlaceholder, instrumentalOnlyMusic]);
};

const useBannerManagement = ({ addBanner, removeBanner, updateBanner }) => {
  return useCallback(({hasUser, isPlus, isEmailVerified, isGhost, hasLoadedServerUser, userTotalAvailableCredits, creditCost, percentageOfInputOverMax, percentageOfConversationFull, isOverMessagesLimit, effectiveMaxInputTokens, currentInputTokens, hasAttachment, requiresImageInput, hasImageAttachment, requiresAudioInput, hasAudioAttachment, isMobileDevice}) => {
    try {
      const totalAvailable = userTotalAvailableCredits;
      const totalRequired = creditCost;

      // Auth CTA banner — shown to anonymous (logged-out) visitors so the
      // composer still works as an experience preview while inviting sign-up.
      // The legacy `ghost-banner` was retired with ghost users; we make sure
      // any stale ghost-banner entries are cleared too.
      if (!hasUser) {
        addBanner('auth-cta-banner');
      } else {
        removeBanner('auth-cta-banner');
      }
      removeBanner('ghost-banner');

      // Required-file requirements (image / audio) used to drive dedicated
      // banners. They are now surfaced as ghost cards in the chat input's
      // attachment row (see `FileParamGhostCard`), so the banners are no
      // longer added. We still ensure any leftover banner state is cleared
      // so existing conversations transitioning to the new UI don't keep
      // stale banners.
      removeBanner('requires-image-banner');
      removeBanner('requires-audio-banner');

      const percentageBuffer = 2;
      if (percentageOfInputOverMax > percentageBuffer) {
        addBanner('message-context-banner', {
          percentageOver: percentageOfInputOverMax,
          effectiveMaxInputTokens,
          currentInputTokens,
          hasAttachment,
        });
        updateBanner('message-context-banner', {
          percentageOver: percentageOfInputOverMax,
          effectiveMaxInputTokens,
          currentInputTokens,
          hasAttachment,
        });
      } else {
        removeBanner('message-context-banner');
      }

      const isLargePrompt =
        currentInputTokens >= CONTEXT_NOTIFICATION_LIMITS.LARGE_INPUT_WARNING
        && percentageOfInputOverMax <= percentageBuffer;
      if (isLargePrompt) {
        addBanner('large-prompt-banner', { estimatedTokens: currentInputTokens });
        updateBanner('large-prompt-banner', { estimatedTokens: currentInputTokens });
      } else {
        removeBanner('large-prompt-banner');
      }

      // Conversation-context banner used to fire ONLY after the conversation
      // had already overflowed the model's context window — i.e. after the
      // backend started silently dropping older turns. The new behavior
      // (auto-summarization preserves continuity, never blocks) means we want
      // a proactive heads-up around 70% so the user knows compaction is about
      // to kick in, not a reactive scolding once it's too late.
      const shouldShowConversationContextBanner =
        percentageOfConversationFull >= CONTEXT_NOTIFICATION_LIMITS.CONTEXT_LENGTH_WARN_PCT
        || isOverMessagesLimit;
      if (shouldShowConversationContextBanner) {
        addBanner('conversation-context-banner');
      } else {
        removeBanner('conversation-context-banner');
      }
      
      // Credits are not persisted; wait for server data before treating zero as real.
      // Anonymous visitors don't have credits at all, so we skip the low-credits
      // nag and let the auth-cta-banner carry the messaging instead.
      if (!hasLoadedServerUser || !hasUser) {
        removeBanner('low-credits-banner');
        removeBanner('credits-banner');
      } else {
        // Unverified free accounts start at 30 credits — that is not "running
        // low". Show the verify-email promo instead of the yellow upsell.
        // Keep the conversation clear of credit upsells until the balance is exhausted.
        const needsVerifyPromo = !isPlus && !isGhost && !isEmailVerified;
        if (needsVerifyPromo) {
          addBanner('low-credits-banner');
        } else if (!isPlus && isEmailVerified && Number.isFinite(totalAvailable) && totalAvailable <= 0) {
          addBanner('low-credits-banner');
        } else if (isPlus && Number.isFinite(totalAvailable) && totalAvailable <= 0) {
          addBanner('low-credits-banner');
        } else {
          removeBanner('low-credits-banner');
        }
      }

      if (hasLoadedServerUser && hasUser && isPlus) {
        // Handle credits banner for Plus users when they don't have enough credits
        if (totalRequired && totalAvailable < totalRequired) {
          addBanner('credits-banner', {
            available: totalAvailable,
            required: totalRequired
          });
          updateBanner('credits-banner', {
            available: totalAvailable,
            required: totalRequired
          });
        } else {
          removeBanner('credits-banner');
        }
      } else {
        // Clear a previous subscriber quote after an account/plan change.
        removeBanner('credits-banner');
      }
    } catch (error) {
      console.error('Error managing banners:', error);
    }
  }, [addBanner, removeBanner, updateBanner]);
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * ChatInput Component
 * @param {ChatInputProps} props
 */
export default function ChatInput({
  placeholder,
  forcePlaceholder = false,
  value,
  showGhostBanner = false,
  modelIds,
  agentIds,
  fileIds,
  onSend,
  onCancel,
  onFocus,
  onBlur,
  autoFocus = false,
  forceInputStyle,
  conversationId,
  collectionId,
  isInferencing: propIsInferencing = false,
  // Coworker threads keep the send action live while a run is in flight —
  // Enter queues until this task finishes. Send now interrupts (steer).
  // An empty box still exposes Stop on its own control.
  sendWhileInferencing = false,
  // Work: Enter queues while a run is live. Send now is the steer control.
  // Stop stays on its own control so Return never cancels.
  workFollowUpMode = false,
  onSendNow,
  isLoading: propIsLoading = false,
  isDisabled: propIsDisabled = false,
  withModels = true,
  hideVoiceMode = false,
  hideAgentTags = false,
  showBotPicker = false,
  isWorkMode = false,
  workGoal,
  onGoalSend,
  onGoalControl,
  goalControlsDisabled = false,
  isCompact = false,
  modelInputs,
  modelOutputs,
  hideParameterIds = [],
  editFileIds = [],
  onChangeText: onChangeTextProp,
}) {
  // ============================================================================
  // Store Actions & State
  // ============================================================================
  
  // const {
  //   getCurrentState,
  // } = useChatInputStore();

  // OK
  const addChangeListener = useChatInputStore(state => state.addChangeListener);
  const calculateMessageCredits = useChatInputStore(state => state.calculateMessageCredits);
  const setContext = useChatInputStore(state => state.setContext);
  const getFormattedMessage = useChatInputStore(state => state.getFormattedMessage);
  const toggleButton = useChatInputStore(state => state.toggleButton);
  const setParameter = useChatInputStore(state => state.setParameter);
  const resetCreditCalculation = useChatInputStore(state => state.resetCreditCalculation);
  const setSelectedModels = useChatInputStore(state => state.setSelectedModels);
  const setSelectedAgents = useChatInputStore(state => state.setSelectedAgents);
  const setText = useChatInputStore(state => state.setText);
  const setFiles = useChatInputStore(state => state.setFiles);
  const addFile = useChatInputStore(state => state.addFile);
  const removeFile = useChatInputStore(state => state.removeFile);
  const addFileById = useChatInputStore(state => state.addFileById);
  const setQuote = useChatInputStore(state => state.setQuote);
  const setEditFileIds = useChatInputStore(state => state.setEditFileIds);
  const clearInput = useChatInputStore(state => state.clearInput);
  const setInputPlaceholder = useChatInputStore(state => state.setInputPlaceholder);
  const addBanner = useChatInputStore(state => state.addBanner);
  const removeBanner = useChatInputStore(state => state.removeBanner);
  const updateBanner = useChatInputStore(state => state.updateBanner);
  const isMissingRequiredParameters = useChatInputStore(state => state.isMissingRequiredParameters);
  const setHighlightedMissingParameters = useChatInputStore(state => state.setHighlightedMissingParameters);
  const highlightedMissingParameterIds = useChatInputStore(state => state.highlightedMissingParameterIds);

  // const currentContextId = useChatInputStore(state => state.currentContextId);

  // File actions
  const { t } = useTranslation();

  const { uploadFile, isFileAllowed, fetchAllowedFileTypes, hasCreditsForFileUpload } = useFileStore(state => state.actions);
  // Polling function for document processing status (optional - may not exist in all environments)
  const pollFileProcessingStatus = useFileStore(state => state.actions?.pollFileProcessingStatus);
  const { addToast } = useToastStore(state => state.actions);

  // ============================================================================
  // User Data & Input State
  // ============================================================================

  const userDefaultTextModelId = useAuthStore(state => {
    const raw = state.user?.defaultModel?.text;
    if (!raw) return null;
    return typeof raw === 'string' ? raw : raw.id || null;
  });
  const userTotalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable);
  const hasLoadedServerUser = useAuthStore(state => state.hasLoadedServerUser);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isEmailVerified = useAuthStore(state => !!state.user?.flags?.isEmailVerified);
  const isGhost = useAuthStore(state => !!state.user?.flags?.isGhost);
  const currentContextId = conversationId || 'new';
  const composerRootRef = useRef(null);
  const composerFocusedRef = useRef(false);
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  const requireAuth = useRequireAuth();
  // console.log('---CHATINPUT...RENDER currentContextId', currentContextId);

  const files = useChatInputStore((s) => s.inputStates[currentContextId]?.files ?? EMPTY_INPUT_LIST);
  const selectedParameters = useChatInputStore((s) => s.inputStates[currentContextId]?.selectedParameters ?? EMPTY_INPUT_PARAMETERS);
  const text = useChatInputStore((s) => s.inputStates[currentContextId]?.text ?? '');
  const quote = useChatInputStore((s) => s.inputStates[currentContextId]?.quote ?? '');
  const selectedButtons = useChatInputStore((s) => s.inputStates[currentContextId]?.selectedButtons ?? EMPTY_INPUT_LIST);
  const selectedModelIds = useChatInputStore((s) => s.inputStates[currentContextId]?.selectedModelIds ?? EMPTY_INPUT_LIST);
  const selectedAgentIds = useChatInputStore((s) => s.inputStates[currentContextId]?.selectedAgentIds ?? EMPTY_INPUT_LIST);
  const userExplicitAutoMode = useChatInputStore((s) => s.inputStates[currentContextId]?.userExplicitAutoMode ?? false);
  const inputPlaceholder = useChatInputStore((s) => s.inputStates[currentContextId]?.inputPlaceholder ?? t('chatInput.placeholderDefault'));
  const banners = useChatInputStore((s) => s.inputStates[currentContextId]?.banners ?? EMPTY_INPUT_LIST);
  const creditCalculation = useChatInputStore((s) => s.inputStates[currentContextId]?.creditCalculation?.isCalculating ?? false);
  const creditError = useChatInputStore((s) => s.inputStates[currentContextId]?.creditCalculation?.error ?? null);
  const creditCost = useChatInputStore((s) => s.inputStates[currentContextId]?.creditCalculation?.cost ?? null);
  const creditHold = useChatInputStore((s) => s.inputStates[currentContextId]?.creditCalculation?.authorizationCost ?? null);

  const getActionParameters = useChatInputStore(s => s.getActionParameters);

  const inputState = {
    text,
    files,
    selectedParameters,
    selectedButtons,
    selectedModelIds,
    selectedAgentIds,
    inputPlaceholder,
    creditCalculation: {
      isCalculating: creditCalculation,
      error: creditError,
      cost: creditCost,
      authorizationCost: creditHold,
    },
    banners,
    quote,
  };

  // console.log('---CHATINPUT...RENDER inputState.quote', quote);

  const parameters = getActionParameters(inputState);
  // Filter out hidden parameters if hideParameterIds is provided
  const filteredParameters = useMemo(() => {
    if (!hideParameterIds || hideParameterIds.length === 0) return parameters;
    return parameters.filter(p => !hideParameterIds.includes(p?.id) && !hideParameterIds.includes(p?.name));
  }, [parameters, hideParameterIds]);
  inputState.parameters = filteredParameters;

  const missingRequiredParameters = useMemo(() => {
    try {
      const result = isMissingRequiredParameters();
      return result;
    } catch (_) {
      return { isMissing: false, missingParameters: [] };
    }
  }, [
    inputState.parameters,
    inputState.selectedParameters,
    inputState.files,
    inputState.text,
    inputState.selectedModelIds,
    inputState.selectedAgentIds,
  ]);

  // console.log('---CHATINPUT...RENDER inputState', inputState.parameters);

  // ============================================================================
  // Models & Agents
  // ============================================================================

  const modelSelector = useCallback(state => state.models, []);
  const agentSelector = useCallback(state => state.agents, []);

  const models = useModelStore(modelSelector);
  const agents = useAgentStore(agentSelector);

  const selectedModels = useMemo(() => {
    return inputState.selectedModelIds.reduce((acc, id) => {
      if (models[id]) acc.push(models[id]);
      return acc;
    }, []);
  }, [models, inputState.selectedModelIds]);

  const selectedAgents = useMemo(() => {
    return inputState.selectedAgentIds.reduce((acc, id) => {
      if (agents[id]) acc.push(agents[id]);
      return acc;
    }, []);
  }, [agents, inputState.selectedAgentIds]);

  const minSelectedModelsMaxInputTokens = useMemo(() => {
    const values = selectedModels
      .map(m => m?.maxInputTokens)
      .filter(v => typeof v === 'number' && !isNaN(v));
    return values.length ? Math.min(...values) : null;
  }, [selectedModels]);

  const minSelectedModelsContextWindow = useMemo(() => {
    const values = selectedModels
      .map(m => m?.contextWindow)
      .filter(v => typeof v === 'number' && !isNaN(v));
    return values.length ? Math.min(...values) : null;
  }, [selectedModels]);

  const effectiveMaxInputTokens = useMemo(() => {
    return (typeof minSelectedModelsMaxInputTokens === 'number' && !isNaN(minSelectedModelsMaxInputTokens))
      ? minSelectedModelsMaxInputTokens
      : CONTEXT_NOTIFICATION_LIMITS.INPUT_LENGTH_MAX;
  }, [minSelectedModelsMaxInputTokens]);

  const effectiveContextWindow = useMemo(() => {
    return (typeof minSelectedModelsContextWindow === 'number' && !isNaN(minSelectedModelsContextWindow))
      ? minSelectedModelsContextWindow
      : CONTEXT_NOTIFICATION_LIMITS.CONTEXT_LENGTH_WARNING;
  }, [minSelectedModelsContextWindow]);


  // ============================================================================
  // Chat State & Derived State
  // ============================================================================

  const chat = useChatStore(useCallback(createChatSelector(conversationId), [conversationId]));
  // New Chat / collection drafts are not a live thread. On native the home
  // composer can stay mounted under a Work chat; inheriting that thread's
  // isInferencing turns Send into a spinner that cannot start a new chat.
  const isDraftComposer = isDraftComposerConversationId(conversationId);
  const hasPendingMessage = useMemo(() => {
    if (isDraftComposer) return false;
    return chat?.messages?.some(m =>
      m.role === 'user' &&
      m.isPending !== false &&
      (!m.id || (typeof m.id === 'string' && m.id.startsWith('msg_') && !m._id))
    );
  }, [chat, isDraftComposer]);
  
  const isInferencing = !isDraftComposer && (
    chat?.isInferencing || chat?.isInferencingWaiting || propIsInferencing
  );
  
  // Check if any files are still uploading
  const hasUploadingFiles = useMemo(() => {
    return inputState.files?.some(file => file?.isUploading === true);
  }, [inputState.files]);

  // Check if any document files are still being processed (pending/processing status)
  const hasProcessingFiles = useMemo(() => {
    return inputState.files?.some(file => {
      if (!file || file.isUploading) return false;
      const status = file.processingStatus?.status;
      return status === 'pending' || status === 'processing';
    });
  }, [inputState.files]);

  // True when EVERY selected model declares it doesn't need a text prompt
  // (transcription / upscale / image-to-3D, etc.). When true, the chat
  // composer hides its textarea + dictation toggle and lets the user send
  // with empty text (the file params + custom params drive the request).
  // Multi-select with a chat model falls back to "show textarea" since the
  // chat model still needs the prompt.
  const hideTextInput = useMemo(() => {
    if (!Array.isArray(selectedModels) || selectedModels.length === 0) return false;
    return selectedModels.every(m => m?.capabilities?.input?.hidesTextInput === true);
  }, [selectedModels]);

  // Music models (e.g. MiniMax Music 2.6) expose an `is_instrumental` toggle.
  // When it's on, lyrics are ignored — the backend builds the track from the
  // description param alone — so the composer text becomes optional and the
  // placeholder shouldn't demand lyrics. Mixed selections with models that
  // lack the toggle (e.g. a chat model) still require text.
  const instrumentalOnlyMusic = useMemo(() => {
    const models = Array.isArray(selectedModels) ? selectedModels.filter(Boolean) : [];
    if (models.length === 0) return false;
    const params = Array.isArray(inputState.parameters) ? inputState.parameters : [];
    const isTruthy = (v) =>
      v === true || v === 1 ||
      String(v).toLowerCase() === 'true' ||
      String(v).toLowerCase() === 'on' ||
      String(v) === '1';
    const exposesToggle = (model) =>
      (Array.isArray(model?.parameters) ? model.parameters : [])
        .some((p) => (p?.name || p?.id) === 'is_instrumental');
    if (!models.every(exposesToggle)) return false;
    const instParams = params.filter((p) => p?.name === 'is_instrumental');
    return instParams.length > 0 && instParams.every((p) => isTruthy(p?.value));
  }, [selectedModels, inputState.parameters]);

  // Inline params (`ui.displayPosition === 'inline'`) flattened across the
  // selected models, deduped by `name` so a multi-model selection that
  // shares a parameter (e.g. both Music 2.0 + 2.6 expose `description`)
  // only renders one row. Hoisted up here so the send-time gate below can
  // depend on it; UI consumers further down read the same memo.
  const inlineParams = useMemo(() => {
    if (!Array.isArray(inputState.parameters)) return [];
    const seen = new Set();
    const out = [];
    for (const p of inputState.parameters) {
      if (!isInlineParam(p)) continue;
      const key = p?.name || p?.id;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(p);
    }
    out.sort((a, b) => {
      const ao = typeof a?.ui?.order === 'number' ? a.ui.order : 999;
      const bo = typeof b?.ui?.order === 'number' ? b.ui.order : 999;
      return ao - bo;
    });
    return out;
  }, [inputState.parameters]);

  // True when every *required* inline param has a non-empty string value.
  // Pairs with `hasAllRequiredFiles` for the send-button gating below.
  const hasAllRequiredInlineParams = useMemo(() => {
    for (const p of inlineParams) {
      if (!p?.validation?.required) continue;
      const v = typeof p?.value === 'string' ? p.value.trim() : '';
      if (!v) return false;
    }
    return true;
  }, [inlineParams]);

  // Whether every required file-typed custom parameter across the selected
  // models has a matching attachment. Inlines the matcher so the send-time
  // gating below doesn't depend on hooks declared further down the file.
  const hasAllRequiredFiles = useMemo(() => {
    if (!Array.isArray(selectedModels) || selectedModels.length === 0) return true;
    const files = Array.isArray(inputState.files) ? inputState.files : [];
    const matches = (file, param) => {
      const expected = param?.validation?.file?.fileType;
      if (!expected || expected === 'any') return true;
      const ftype = file?.fileType || '';
      if (ftype === expected) return true;
      const mime = (file?.mimeType || file?.type || file?.mime || '').toString();
      if (expected === 'image' && (ftype === 'image' || mime.startsWith('image/'))) return true;
      if (expected === 'audio' && (ftype === 'audio' || mime.startsWith('audio/'))) return true;
      if (expected === 'video' && (ftype === 'video' || mime.startsWith('video/'))) return true;
      if (expected === 'document' && (
        mime === 'application/pdf'
        || mime.startsWith('text/')
        || mime.startsWith('application/msword')
        || mime.startsWith('application/vnd.')
        || mime.startsWith('application/rtf')
      )) return true;
      return false;
    };
    for (const model of selectedModels) {
      const params = Array.isArray(model?.parameters) ? model.parameters : [];
      for (const p of params) {
        if (!FILE_COMPONENT_TYPES.has(p?.ui?.component)) continue;
        if (!p?.validation?.required) continue;
        if (!files.some((f) => matches(f, p))) return false;
      }
    }
    return true;
  }, [selectedModels, inputState.files]);

  // Send is enabled when there's something the model can act on:
  //   - text in the box (chat models, hybrid models), OR
  //   - we're already inferencing / have a pending message (cancel path), OR
  //   - the model declares `hidesTextInput` AND every required file is in
  //     place (transcription / upscale / image-to-3D — file params drive
  //     the entire request)
  // Required inline params are intentionally NOT included here — keeping
  // the button clickable lets the send handler surface a targeted toast
  // ("Music description is required.") rather than a silent no-op.
  const isEnabled = (
    hasPendingMessage
    || isInferencing
    || !!inputState.text?.trim()
    || (hideTextInput && hasAllRequiredFiles)
    || instrumentalOnlyMusic
  ) && !propIsDisabled && !hasUploadingFiles && !hasProcessingFiles;
  const isLoading = propIsLoading || isInferencing;
  // Work threads keep optimistic `msg_*` user bubbles with isPending while
  // the run is live. Those must not force Enter to Stop — if the box has
  // content and sendWhileInferencing is on, Enter steers instead.
  const hasComposerContent = !!inputState.text?.trim()
    || (Array.isArray(inputState.files) && inputState.files.length > 0);
  const canSendWhileLive = sendWhileInferencing && hasComposerContent;
  const performableAction = getComposerPerformableAction({
    hasPendingMessage,
    isInferencing,
    canSendWhileLive,
    canCancel: !!onCancel,
  });

  // derive message count for upgrade banner logic
  const messageCount = chat?.messages?.length || 0;
  const isOverMessagesLimit = useMemo(() => {
    return messageCount > CONTEXT_NOTIFICATION_LIMITS.MESSAGES_WARNING;
  }, [messageCount]);

  // ============================================================================
  // Custom Hooks Setup
  // ============================================================================

  const currentContextTokens = {
    input: tokenCounter(inputState.text),
    conversation: chat?.stats?.totalTokens || 0,
  };

  const hasAttachment = useMemo(() => {
    return Array.isArray(inputState.files) && inputState.files.length > 0;
  }, [inputState.files]);

  // Required-file detection now flows from each model's file-typed custom
  // params (e.g. `audio_file` / `image_file` with `validation.required: true`).
  // The legacy `capabilities.input.requiresImage` / `requiresAudio` flags have
  // been retired now that file params own this contract. The component set is
  // imported from `fileParamUtils` so it stays in sync with the ghost-card
  // and bottom-bar filtering logic.
  const fileMatchesParam = useCallback((file, param) => {
    const expected = param?.validation?.file?.fileType;
    if (!expected || expected === 'any') return true;
    const ftype = file?.fileType || '';
    if (ftype === expected) return true;
    const mime = (file?.mimeType || file?.type || file?.mime || '').toString();
    if (expected === 'image' && (ftype === 'image' || mime.startsWith('image/'))) return true;
    if (expected === 'audio' && (ftype === 'audio' || mime.startsWith('audio/'))) return true;
    if (expected === 'video' && (ftype === 'video' || mime.startsWith('video/'))) return true;
    if (expected === 'document' && (
      mime === 'application/pdf'
      || mime.startsWith('text/')
      || mime.startsWith('application/msword')
      || mime.startsWith('application/vnd.')
      || mime.startsWith('application/rtf')
    )) return true;
    return false;
  }, []);

  // Required file params across all selected models that don't yet have a
  // matching attachment. Used to gate send and (legacy) toast.
  const unsatisfiedRequiredFileParams = useMemo(() => {
    if (!Array.isArray(selectedModels) || selectedModels.length === 0) return [];
    const files = Array.isArray(inputState.files) ? inputState.files : [];
    const out = [];
    for (const model of selectedModels) {
      const params = Array.isArray(model?.parameters) ? model.parameters : [];
      for (const p of params) {
        if (!FILE_COMPONENT_TYPES.has(p?.ui?.component)) continue;
        if (!p?.validation?.required) continue;
        if (files.some((f) => fileMatchesParam(f, p))) continue;
        out.push({ modelId: model.id || model._id, modelName: model.name, param: p });
      }
    }
    return out;
  }, [selectedModels, inputState.files, fileMatchesParam]);

  const hasUnsatisfiedRequiredFiles = unsatisfiedRequiredFileParams.length > 0;
  const requiresImageInput = useMemo(
    () => unsatisfiedRequiredFileParams.some(({ param }) => param?.validation?.file?.fileType === 'image'),
    [unsatisfiedRequiredFileParams]
  );
  const requiresAudioInput = useMemo(
    () => unsatisfiedRequiredFileParams.some(({ param }) => param?.validation?.file?.fileType === 'audio'),
    [unsatisfiedRequiredFileParams]
  );

  // Kept for downstream consumers that still inspect these (toast guard /
  // existing predicates). They now flip alongside the unsatisfied predicate
  // since the ghost card already communicates the requirement visually.
  const hasImageAttachment = !requiresImageInput;
  const hasAudioAttachment = !requiresAudioInput;

  // Required inline params with empty values — gate send and drive the
  // toast message, parallel to `unsatisfiedRequiredFileParams`.
  const unsatisfiedRequiredInlineParams = useMemo(() => {
    return inlineParams.filter((p) => {
      if (!p?.validation?.required) return false;
      const val = typeof p?.value === 'string' ? p.value.trim() : '';
      return val.length === 0;
    });
  }, [inlineParams]);
  const hasUnsatisfiedRequiredInlineParams = unsatisfiedRequiredInlineParams.length > 0;

  const percentageOfInputOverMax = useMemo(() => {
    if (!effectiveMaxInputTokens || effectiveMaxInputTokens <= 0) return 0;
    const over = currentContextTokens.input - effectiveMaxInputTokens;
    if (over <= 0) return 0;
    const pct = (over / effectiveMaxInputTokens) * 100;
    return Math.max(0, Math.min(999, Math.round(pct)));
  }, [currentContextTokens.input, effectiveMaxInputTokens]);

  // What % of the model's context window is currently in use? (Was previously
  // "% over the limit" — only non-zero after overflow, which made the warning
  // banner reactive instead of proactive.) The conversation banner now
  // triggers at CONTEXT_LENGTH_WARN_PCT of this value so users get a heads-up
  // before auto-summarization kicks in, rather than only after the backend
  // has already had to compact things.
  const percentageOfConversationFull = useMemo(() => {
    if (!effectiveContextWindow || effectiveContextWindow <= 0) return 0;
    const pct = (currentContextTokens.conversation / effectiveContextWindow) * 100;
    return Math.max(0, Math.min(999, Math.round(pct)));
  }, [currentContextTokens.conversation, effectiveContextWindow]);

  // const calculateCreditsDebounced = () => {};
  // const updatePlaceholderForSelection = () => {};

  const { calculateCredits, cleanup: cleanupCredits } = useCreditCalculation({
    text: inputState.text,
    files: inputState.files,
    selectedModelIds: inputState.selectedModelIds,
    selectedAgentIds: inputState.selectedAgentIds,
    conversationId,
    collectionId,
    calculateMessageCredits,
    resetCreditCalculation,
    getFormattedMessage,
    userId
  });
  
  const updatePlaceholderForSelection = usePlaceholderManagement(setInputPlaceholder, instrumentalOnlyMusic);

  // When the instrumental toggle flips, refresh the composer placeholder so
  // the lyrics box switches between "Write the lyrics…" and the optional
  // hint without requiring a model re-selection.
  const prevInstrumentalOnlyMusicRef = useRef(instrumentalOnlyMusic);
  useEffect(() => {
    if (prevInstrumentalOnlyMusicRef.current === instrumentalOnlyMusic) return;
    prevInstrumentalOnlyMusicRef.current = instrumentalOnlyMusic;
    updatePlaceholderForSelection(inputState.selectedModelIds, inputState.selectedAgentIds);
  }, [instrumentalOnlyMusic, inputState.selectedModelIds, inputState.selectedAgentIds, updatePlaceholderForSelection]);

  const { isXS } = useResponsiveness();
  const isMobileDevice = Platform.OS !== 'web' || isXS;

  const manageBanners = useBannerManagement({ addBanner, removeBanner, updateBanner });

  // ============================================================================
  // Event Handlers
  // ============================================================================

  // Ensures we trigger an initial credits calculation when models are already
  // selected (e.g., returning to a chat where selection was persisted) and no
  // store change event fires.
  const initialCreditsEnsuredRef = useRef({ contextId: null, modelKey: null });
  const sendInFlightRef = useRef(false);
  const composerSenderRef = useRef(null);

  // Native New Chat stays mounted under a Work thread. If a launch/send was
  // still marked in-flight when we come back, Send would no-op even after
  // the spinner is cleared.
  useEffect(() => {
    if (isDraftComposer && !propIsLoading) sendInFlightRef.current = false;
  }, [isDraftComposer, propIsLoading]);

  const handleOnFocus = useCallback(() => {
    composerFocusedRef.current = true;
    onFocus?.();
  }, [onFocus]);

  const handleOnBlur = useCallback(() => {
    composerFocusedRef.current = false;
    onBlur?.();
  }, [onBlur]);
  
  const handleOnSetSelectedAgents = useCallback((selectedAgentIds) => {
    // Ensure correct context before updating (see handleOnSetSelectedModels comment)
    setContext(currentContextId);
    setSelectedAgents(selectedAgentIds);
  }, [setSelectedAgents, setContext, currentContextId]);

  const handleOnSetSelectedModels = useCallback((selectedModelIds) => {
    // Ensure the store's currentContextId matches this component's context
    // before updating. On iOS, multiple ChatInput instances can be mounted
    // (back-stack screens), and the global currentContextId may point to a
    // different screen's context when the model selector modal closes.
    setContext(currentContextId);
    setSelectedModels(selectedModelIds);
  }, [setSelectedModels, setContext, currentContextId]);

  // IMPORTANT: This callback must be STABLE (no inputState.text in deps).
  // Recreating it on every keystroke causes the TextInput component to re-render,
  // which on iOS triggers a controlled-TextInput race condition where the native
  // text field flashes a stale `value` and fires a spurious onChangeText(""),
  // creating a ping-pong effect that deletes the first character.
  // The store's setText() already deduplicates identical values internally.
  //
  // CRITICAL: We must call setContext() before setText() to ensure the store's
  // global currentContextId matches this component's context. On iOS, multiple
  // ChatInput instances remain mounted in the back-stack, and another screen's
  // setContext(otherContextId) may have changed the global ID. Without this
  // guard, setText() writes to the wrong context — the TextInput reads from
  // inputStates['new'] but setText() writes to inputStates[someOtherContext],
  // so the value stays '' and the typed character "instantly deletes".
  // setContext() is a no-op when the ID already matches (O(1) bail-out).
  // All three deps (setText, setContext, currentContextId) are referentially
  // stable, so this callback is never recreated between renders.
  // Read the controlled `value` from a ref so home chips stay in sync without
  // putting `value` in the dependency list (that recreated the callback every
  // keystroke and ping-ponged MentionsInput).
  const valueRef = useRef(value);
  valueRef.current = value;
  const handleOnChangeText = useCallback((text) => {
    setContext(currentContextId);
    setText(text);
    if (onChangeTextProp && text !== (valueRef.current ?? '')) {
      onChangeTextProp(text);
    }
  }, [setText, setContext, currentContextId, onChangeTextProp]);

  const selectGoalWorkMode = useCallback(() => {
    // Home and collection sends read this fresh state and retain their normal
    // launch, project, attachment, authentication, and subscription checks.
    if (blockIfWorkModeDisabled()) return false;
    if (isDraftComposer) useCoworkerStore.getState().setComposerMode('coworker');
    return true;
  }, [isDraftComposer]);
  const goalCommand = useGoalCommand({
    value: inputState.text,
    contextId: currentContextId,
    enabled: isWorkMode || isDraftComposer,
    onChangeText: handleOnChangeText,
    onSelectWorkMode: selectGoalWorkMode,
  });

  // IME, password-manager, and automation fills set <textarea>.value without
  // MentionsInput onChange. Send then stays hidden (store empty) and a later
  // click no-ops. Sync the visible box into this context when the store is blank.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return undefined;
    const syncDomComposer = (event) => {
      const root = composerRootRef.current;
      if (!root || !root.contains(event.target) || !event.target.matches?.('textarea[data-gab-composer]')) return;
      const storeText = String(
        useChatInputStore.getState().inputStates?.[currentContextId]?.text || ''
      ).trim();
      if (storeText) return;
      const domText = readWebComposerDomText(document, root).trim();
      if (!domText) return;
      setContext(currentContextId);
      setText(domText);
    };
    document.addEventListener('input', syncDomComposer, true);
    document.addEventListener('change', syncDomComposer, true);
    return () => {
      document.removeEventListener('input', syncDomComposer, true);
      document.removeEventListener('change', syncDomComposer, true);
    };
  }, [currentContextId, setText, setContext]);

  const handleOnActionPress = useCallback((options) => {
    const goalObjective = options?.goalObjective;
    const requested = composerSenderRef.current;
    composerSenderRef.current = null;
    const senderMode = resolveWorkComposerSender(requested, { workFollowUpMode });

    // Read live composer text before the render-state `isEnabled` gate.
    // IME / controlled-value desync can leave the store blank while the
    // textarea has a prompt — Work send used to no-op in that window.
    const liveState = useChatInputStore.getState().inputStates[currentContextId] || {};
    let liveText = goalObjective ? '/goal set ' + goalObjective : String(liveState.text || '').trim();
    if (Platform.OS === 'web' && !liveText && composerRootRef.current) {
      const domText = readWebComposerDomText(document, composerRootRef.current).trim();
      if (domText) {
        setContext(currentContextId);
        setText(domText);
        liveText = domText;
      }
    }
    const liveFiles = Array.isArray(liveState.files) ? liveState.files : [];
    const liveHasFiles = liveFiles.length > 0;
    // An ordinary Stop click has no explicit sender. The default resolved
    // sender is "now", so checking that value made Stop a no-op while Work
    // waited for Mac access. Cancellation must also precede send validation.
    if (getComposerPerformableAction({
      hasPendingMessage,
      isInferencing,
      canCancel: !!onCancel,
      canSendWhileLive: sendWhileInferencing && (!!liveText || liveHasFiles),
      requestedSender: requested,
    }) === 'cancel') {
      onCancel?.();
      return;
    }
    const goalSend = goalCommand.prepareSend(liveText);
    // A bare /goal opens the objective prompt and keeps the native keyboard up.
    if (goalSend === 'compose') return false;
    const sendingToWork = isWorkMode || goalSend === 'goal';
    const liveFilesNotReady = liveFiles.some((file) => {
      if (file?.isUploading === true) return true;
      const status = file?.processingStatus?.status;
      return status === 'pending' || status === 'processing';
    });
    if (liveFilesNotReady) return;
    if (!isEnabled && !liveText && !liveHasFiles) return;

    // Leftover Chat model params (image/audio/required fields) are hidden in
    // Work and must not swallow Send.
    if (!sendingToWork && missingRequiredParameters.isMissing) {
      addToast?.(t('chatInput.fillRequiredParameters').replace('%PARAMS%', missingRequiredParameters.missingParameters.map(p => p?.ui?.displayName || p?.name || p?.id).join(', ')), 'error');
      // Highlight the missing parameters with a glow animation
      const missingIds = missingRequiredParameters.missingParameters.map(p => p?.id || p?.name);
      setHighlightedMissingParameters?.(missingIds);
      return;
    }

    // Block send when any required file param (image / audio / etc.) is
    // missing its file. The ghost card already shows what's expected; the
    // toast surfaces the requirement on the send attempt.
    if (!sendingToWork && hasUnsatisfiedRequiredFiles) {
      const firstUnsatisfied = unsatisfiedRequiredFileParams[0];
      const fileType = firstUnsatisfied?.param?.validation?.file?.fileType;
      const displayName = firstUnsatisfied?.param?.ui?.displayName;
      if (fileType === 'audio') {
        addToast?.(t('chatInput.modelRequiresAudio'), 'error');
      } else if (fileType === 'image') {
        addToast?.(t('chatInput.modelRequiresImage'), 'error');
      } else {
        const label = (displayName || fileType || 'file').toString().toLowerCase();
        addToast?.(`This model requires ${label === 'file' ? 'a file' : `a ${label}`}.`, 'error');
      }
      return;
    }

    // Block send when any required inline param (e.g. music description,
    // scene prompt) is empty. The inline row already renders a red border
    // when blurred, this just makes the failure unmistakable on send.
    if (!sendingToWork && hasUnsatisfiedRequiredInlineParams) {
      const names = unsatisfiedRequiredInlineParams
        .map((p) => p?.ui?.displayName || p?.name)
        .filter(Boolean);
      const label = names.join(', ') || 'required field';
      addToast?.(
        t('chatInput.fillRequiredParameters').replace('%PARAMS%', label),
        'error'
      );
      const missingIds = unsatisfiedRequiredInlineParams
        .map((p) => p?.id || p?.name)
        .filter(Boolean);
      if (missingIds.length) setHighlightedMissingParameters?.(missingIds);
      return;
    }

    // Models with `hidesTextInput` (transcription/upscale/image-to-3D) are
    // allowed to send with an empty message body — the file params and
    // custom params carry the entire request. Instrumental-only music mode
    // also sends blank: lyrics are unused and the description param drives
    // the track. Otherwise we still gate on a non-empty trimmed text to
    // avoid firing inferences for blank prompts.
    const hasTextOrCanSendBlank = hideTextInput || instrumentalOnlyMusic || !!liveText || liveHasFiles;
    if (hasTextOrCanSendBlank) {
      // Gate the actual send for anonymous visitors. We deliberately do
      // NOT call clearInput() in the gated path so the user's draft (text,
      // files, parameters) is still in the composer when they come back
      // from the auth flow.
      const doSend = () => {
        if (sendInFlightRef.current) return false;
        sendInFlightRef.current = true;

        lightTap();
        // Pin this composer before reading. Deck / side-by-side forks mount
        // multiple ChatInputs; getFormattedMessage() otherwise uses whichever
        // column last called setContext, so the branched chat can search
        // without the prompt the user just typed.
        setContext(currentContextId);
        const formattedMessage = getFormattedMessage(currentContextId);
        if (goalObjective) formattedMessage.text = liveText;
        // Automation / IME can fill the DOM after MentionsInput last
        // reported onChange. Prefer the live composer text we just synced.
        if (liveText && !String(formattedMessage.text || '').trim()) {
          formattedMessage.text = liveText;
        }
        // Snapshot from the fresh formatted message, not the render-closure
        // `inputState.text`, so a failure restore puts back what was sent.
        const textSnapshot = goalObjective ? (liveState.text || '') : (formattedMessage.text ?? '');

        // Re-validate against the FRESH store state. The `hasTextOrCanSendBlank`
        // gate above uses render-closure state which can be stale right after a
        // previous send cleared the composer (fast double-Enter) — the stale
        // gate passes but the live text is empty, and the backend 400s with
        // "Prompt or file is required".
        if (!hideTextInput && !instrumentalOnlyMusic && !formattedMessage.text?.trim() && !formattedMessage.fileIds?.length) {
          sendInFlightRef.current = false;
          return;
        }

        const sendHandler = workFollowUpMode && senderMode === 'queue'
          ? onSend
          : (workFollowUpMode ? (onSendNow || onSend) : onSend);
        const result = sendHandler?.(formattedMessage);

        // Sync bail-out before dispatch — keep the draft intact.
        if (result === false) {
          sendInFlightRef.current = false;
          return false;
        }

        // Clear immediately so Enter-to-send feels instant. Async handlers
        // (e.g. createConversation on the home composer) used to block here
        // for seconds while the API round-tripped; restore the text only if
        // dispatch ultimately fails.
        clearInput();

        if (result && typeof result.then === 'function') {
          return result
            .then((accepted) => {
              if (accepted !== true) {
                setContext(currentContextId);
                setText(textSnapshot);
              }
              return accepted === true;
            })
            .catch(() => {
              setContext(currentContextId);
              setText(textSnapshot);
              return false;
            })
            .finally(() => {
              sendInFlightRef.current = false;
            });
        }

        sendInFlightRef.current = false;
        return true;
      };
      return requireAuth(doSend, {
        source: 'composer_send',
        title: t('chatInput.authCta.modalTitle'),
        subtitle: t('chatInput.authCta.modalSubtitle'),
        icon: 'message-dots',
      });
    }
  }, [isEnabled, isWorkMode, goalCommand.prepareSend, missingRequiredParameters, hasPendingMessage, isInferencing, onSend, onSendNow, workFollowUpMode, onCancel, sendWhileInferencing, inputState.text, getFormattedMessage, clearInput, setText, setContext, currentContextId, setHighlightedMissingParameters, hasUnsatisfiedRequiredFiles, unsatisfiedRequiredFileParams, hasUnsatisfiedRequiredInlineParams, unsatisfiedRequiredInlineParams, addToast, t, hideTextInput, instrumentalOnlyMusic, requireAuth]);

  const handleStartGoal = useCallback(async objective => {
    composerSenderRef.current = 'now';
    const accepted = await handleOnActionPress({ goalObjective: objective });
    if (accepted === true) goalCommand.finishEditing();
    return accepted === true;
  }, [handleOnActionPress, goalCommand.finishEditing]);

  const handleOnSendNow = useCallback(() => {
    composerSenderRef.current = 'now';
    return handleOnActionPress();
  }, [handleOnActionPress]);

  const handleOnQueue = useCallback(() => {
    composerSenderRef.current = 'queue';
    return handleOnActionPress();
  }, [handleOnActionPress]);

  // Submitting text must never inherit the button's empty-draft Stop action.
  const handleOnSubmit = useCallback(() => {
    if ((hasPendingMessage || isInferencing) && !sendWhileInferencing) return false;
    return workFollowUpMode ? handleOnQueue() : handleOnSendNow();
  }, [hasPendingMessage, isInferencing, sendWhileInferencing, workFollowUpMode, handleOnQueue, handleOnSendNow]);

  useWorkKeyboardShortcuts({
    isWorkMode, rootRef: composerRootRef, focusedRef: composerFocusedRef,
    isRunning: workFollowUpMode,
    onSend: workFollowUpMode ? handleOnQueue : handleOnSendNow,
    onSendNow: handleOnSendNow,
  });

  const handleOnSetQuote = useCallback((quote) => {
    setContext(currentContextId);
    setQuote(quote);
  }, [setQuote, setContext, currentContextId]);

  const handleParameterChange = useCallback((paramId, value, options) => {
    setContext(currentContextId);
    if (paramId === 'action') {
      setInputPlaceholder(value);
    }
    setParameter(paramId, value, options);
  }, [setParameter, setInputPlaceholder, setContext, currentContextId]);

  const startFileProcessingPoll = useCallback((file) => {
    if (!file?.id || !isFileProcessing(file) || !pollFileProcessingStatus) {
      return;
    }

    // Polling updates all composer contexts by file ID. Avoid writing the
    // result through setFiles(), which targets whichever context is active
    // when the request finishes.
    pollFileProcessingStatus(file.id).catch((err) => {
      console.error('[handleOnAddFile] Polling error:', err);
    });
  }, [pollFileProcessingStatus]);

  // Restored drafts, library picks and Add Sources must get the same fallback
  // polling as drag/drop, even when no upload callback runs in this composer.
  useEffect(() => {
    inputState.files?.forEach(startFileProcessingPoll);
  }, [inputState.files, startFileProcessingPoll]);

  const handleOnAddFile = useCallback((file, tempId, uploadMeta) => {
    try {
      const state = useChatInputStore.getState();
      const currentFiles = state.inputStates?.[currentContextId]?.files || [];

      if (uploadMeta?.placeholderReplaced) {
        if (!isWorkMode) {
          try { maybeOpenUploadedFileInEditor(file); } catch (_) { /* best effort */ }
        }
        startFileProcessingPoll(file);
        return;
      }

      const getId = (f) => (typeof f === 'object' ? f?.id : f);
      const tempIndex = tempId ? currentFiles.findIndex(f => getId(f) === tempId) : -1;

      const isDuplicateElsewhere = currentFiles.some((f, i) => {
        if (tempIndex !== -1 && i === tempIndex) return false;
        const sameId = getId(f) && getId(f) === file?.id;
        return !!sameId;
      });

      if (tempIndex !== -1) {
        const newFiles = [...currentFiles];
        if (isDuplicateElsewhere) {
          // Drop the temp if an identical file already exists
          newFiles.splice(tempIndex, 1);
        } else {
          // Replace temp with uploaded file
          newFiles[tempIndex] = file;
        }
        setFiles(newFiles);
        if (!isDuplicateElsewhere && (state.inputStates?.[currentContextId]?.selectedAgentIds?.length || 0) > 0) {
          setSelectedAgents([]);
        }

        // Office files (docx/xlsx/csv/pptx) and editable artifacts open in
        // their editor beside the chat on desktop web, so "improve this"
        // style follow-ups can revise the draft in place. The attachment
        // stays on the message either way.
        if (!isDuplicateElsewhere && !isWorkMode) {
          try { maybeOpenUploadedFileInEditor(file); } catch (_) { /* best effort */ }
        }

        if (!isDuplicateElsewhere) startFileProcessingPoll(file);
        return;
      }

      // No temp: only add if not already present by id or fingerprint
      const alreadyExists = currentFiles.some(f => {
        const sameId = getId(f) && getId(f) === file?.id;
        return !!sameId;
      });
      if (!alreadyExists) {
        addFile(file);
        if ((state.inputStates?.[currentContextId]?.selectedAgentIds?.length || 0) > 0) {
          setSelectedAgents([]);
        }

        // Same desktop-web editor auto-open as the temp-replacement branch —
        // this is the path library selections and direct adds go through.
        if (!isWorkMode) {
          try { maybeOpenUploadedFileInEditor(file); } catch (_) { /* best effort */ }
        }

        startFileProcessingPoll(file);
      }
    } catch (error) {
      handleError(error, 'file addition');
    }
  }, [addFile, setFiles, currentContextId, isWorkMode, startFileProcessingPoll, setSelectedAgents]);

  const handleOnRemoveFile = useCallback((file) => {
    try {
      setContext(currentContextId);
      removeFile(file);
    } catch (error) {
      handleError(error, 'file removal');
    }
  }, [removeFile, setContext, currentContextId]);

  const handleOnToggleButton = useCallback((buttonId) => {
    setContext(currentContextId);
    toggleButton(buttonId);
  }, [toggleButton, setContext, currentContextId]);

  const handleOnSetFiles = useCallback((files) => {
    setContext(currentContextId);
    setFiles(files);
  }, [setFiles, setContext, currentContextId]);

  // Handle files pasted via clipboard (web ClipboardEvent or native clipboard image)
  const handlePasteFiles = useCallback(async (pastedFiles) => {
    // Keep the store pointed at this composer. On iOS, back-stack ChatInputs
    // can steal currentContextId; addFile writes to whatever is current.
    setContext(currentContextId);

    // Anonymous visitors can't upload — pop the auth CTA instead of silently
    // failing with a credits/auth error toast.
    if (!hasUser) {
      requireAuth(() => {}, {
        source: 'composer_paste_files',
        title: t('chatInput.authCta.uploadTitle'),
        subtitle: t('chatInput.authCta.uploadSubtitle'),
        icon: 'cloud-arrow-up',
      });
      return;
    }
    // Check if user has credits before processing any files
    const { hasCredits, message: creditsMessage } = await hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast?.(creditsMessage, 'error');
      return;
    }

    try {
      await fetchAllowedFileTypes();
    } catch (error) {
      // Non-fatal: isFileAllowed defers to server-side validation when the
      // allowlist couldn't be loaded.
      console.error('[ChatInput] Failed to fetch allowed file types:', error);
    }

    const currentFiles = Array.isArray(inputState.files) ? inputState.files : [];
    const remainingSlots = Math.max(0, (MAX_FILES_UPLOADED_IN_MESSAGE || 10) - currentFiles.length);
    if (remainingSlots <= 0) {
      addToast?.(t('chatInput.maxFilesPerMessage').replace('%MAX%', MAX_FILES_UPLOADED_IN_MESSAGE), 'error');
      return;
    }

    const filesToProcess = Array.from(pastedFiles).slice(0, remainingSlots);
    await uploadFilesWithPlaceholders(filesToProcess, {
      collectionId,
      uploadFile,
      isFileAllowed,
      addToast,
      // Same placeholder wiring as library / camera picks. Passing store
      // `addFile` directly skipped handleOnAddFile and could leave an
      // `isUploading` placeholder that keeps Send disabled.
      onAddUploadingFile: (tempFile) => handleOnAddFile(tempFile, tempFile?.id),
      onAddFile: handleOnAddFile,
      t,
      extraTempFields: (file) => ({ previewFile: file }),
    });
  }, [inputState.files, addToast, isFileAllowed, uploadFile, fetchAllowedFileTypes, handleOnAddFile, hasCreditsForFileUpload, hasUser, requireAuth, collectionId, t, setContext, currentContextId]);

  // ============================================================================
  // Effects
  // ============================================================================

  // First... Setup change listener with error boundary
  useEffect(() => {
    let isSubscribed = true;

    debugLog('change listener: subscribing');
    const unsubscribe = addChangeListener((event) => {
      if (!isSubscribed) return;

      try {
        // Only respond to events for this component's context
        const eventContextId = event?.contextId;
        if (eventContextId !== currentContextId) {
          return;
        }

        debugLog('change listener event', {
          type: event?.type,
          contextId: eventContextId,
          textLength: event?.currentState?.text?.length || 0,
          filesCount: Array.isArray(event?.currentState?.files) ? event.currentState.files.length : 0,
          selectedModelIds: event?.currentState?.selectedModelIds,
          selectedAgentIds: event?.currentState?.selectedAgentIds
        });

        if (!isWorkMode && CREDIT_CALCULATION_EVENTS.includes(event.type)) {
          debugLog('→ triggering calculateCredits for', event.type);
          calculateCredits(event.currentState, event.type, event.data);
        }

        if (MODEL_AGENT_SELECTION_EVENTS.includes(event.type)) {
          debugLog('→ updating placeholder for selection due to', event.type);
          updatePlaceholderForSelection(
            event.currentState.selectedModelIds, 
            event.currentState.selectedAgentIds
          );
        }
      } catch (error) {
        handleError(error, 'change listener');
      }
    });

    // console.log('---SUBSCRIBING CHANGE LISTENER', currentContextId);

    return () => {
      isSubscribed = false;
      // console.log("UNSUBSCRIBING CHANGE LISTENER", currentContextId);
      debugLog('change listener: unsubscribing, cleanupCredits + resetCreditCalculation');
      unsubscribe();
      resetCreditCalculation();
      // Clear any pending debounced calculations
      cleanupCredits();
    };
  }, [addChangeListener, calculateCredits, updatePlaceholderForSelection, resetCreditCalculation, cleanupCredits, currentContextId, isWorkMode]);

  // Set context and update placeholder when conversation changes --- GOOD
  useEffect(() => {
    const contextId = conversationId || 'new';
    debugLog('context change → setContext', { conversationId: contextId });
    setContext(contextId);
    // Update placeholder when context changes
    updatePlaceholderForSelection(inputState.selectedModelIds, inputState.selectedAgentIds);
    // Reset credit calculation when conversation changes
    debugLog('context change → resetCreditCalculation + cleanupCredits');
    resetCreditCalculation();
    cleanupCredits();
    // Reset initial credits ensure guard so we re-check on new context
    initialCreditsEnsuredRef.current = { contextId: null, modelKey: null };
  }, [conversationId, setContext, resetCreditCalculation, cleanupCredits]);

  // Now that context is set, manage banners
  useEffect(() => {
    manageBanners({
      hasUser,
      isPlus,
      isEmailVerified,
      isGhost,
      hasLoadedServerUser,
      userTotalAvailableCredits,
      creditCost: creditHold ?? creditCost,
      percentageOfInputOverMax,
      percentageOfConversationFull,
      isOverMessagesLimit,
      effectiveMaxInputTokens,
      currentInputTokens: currentContextTokens.input,
      hasAttachment,
      requiresImageInput,
      hasImageAttachment,
      requiresAudioInput,
      hasAudioAttachment,
      isMobileDevice,
    });
  }, [
    manageBanners,
    hasUser,
    isPlus,
    isEmailVerified,
    isGhost,
    hasLoadedServerUser,
    userTotalAvailableCredits,
    creditCost,
    creditHold,
    conversationId,
    messageCount,
    percentageOfInputOverMax,
    percentageOfConversationFull,
    isOverMessagesLimit,
    effectiveMaxInputTokens,
    currentContextTokens.input,
    hasAttachment,
    requiresImageInput,
    hasImageAttachment,
    requiresAudioInput,
    hasAudioAttachment,
    isMobileDevice,
  ]);

  // Apply user's default text model when the component mounts and the store
  // hasn't already applied it (e.g., if context was pre-initialized before the
  // user/model data loaded). The store's setContext also applies the default
  // model when creating new input states, so this effect is primarily a
  // fallback for timing races (user data loads after setContext ran) and for
  // when the default model changes while mounted.
  // Skip if the user explicitly chose Auto mode via the model switcher.
  useEffect(() => {
    if (isWorkMode) return;
    if ((!modelIds || modelIds.length === 0) && userDefaultTextModelId && selectedModelIds.length === 0 && !userExplicitAutoMode) {
      setContext(currentContextId);
      // isSeed: programmatic default, not an explicit user choice — must not
      // set modelSelectionTouched or it would block restoring the
      // conversation's last-used model from history.
      setSelectedModels([userDefaultTextModelId], { clearAgents: false, isSeed: true });
    }
  }, [conversationId, modelIds, userDefaultTextModelId, setSelectedModels, setContext, currentContextId, selectedModelIds.length, userExplicitAutoMode, isWorkMode]);

  // Sync with external value prop --- GOOD
  useEffect(() => {
    if (value === undefined) return;
    setText(value);
  }, [value, setText]);

  // Sync with external model IDs --- GOOD
  useEffect(() => {
    if (modelIds?.length > 0) {
      // A prop mirror is not a user choice: it re-runs when the user picks a
      // bot and must not clear that selection the way the model picker does.
      setSelectedModels(modelIds, { clearAgents: false });
      updatePlaceholderForSelection(modelIds, inputState.selectedAgentIds);
    }
  }, [modelIds, setSelectedModels, updatePlaceholderForSelection, inputState.selectedAgentIds]);

  // Sync with external agent IDs --- GOOD
  useEffect(() => {
    if (agentIds?.length > 0) {
      setSelectedAgents(agentIds);
      updatePlaceholderForSelection(inputState.selectedModelIds, agentIds);
    }
  }, [agentIds, setSelectedAgents, updatePlaceholderForSelection, inputState.selectedModelIds]);

  // Handle file IDs --- GOOD
  useEffect(() => {
    if (fileIds?.length > 0) {
      setFiles([]);
      fileIds.forEach(id => addFileById(id));
    }
  }, [fileIds, setFiles, addFileById]);

  // Sync editFileIds prop to store (e.g., for Lightbox inpainting)
  // Only set when explicitly provided (non-empty) to avoid overwriting from other ChatInput instances
  useEffect(() => {
    if (Array.isArray(editFileIds) && editFileIds.length > 0) {
      setEditFileIds(editFileIds);
    }
  }, [editFileIds, setEditFileIds]);

  // Get chat messages to find last used model
  const currentChat = useChatStore(state => conversationId ? state.chats.find(c => c.id === conversationId) : null);

  // REMOVED: Effect to set initial model selection from chat history
  //
  // The model selector should ONLY change when the user explicitly selects a model.
  // We do NOT sync from message history because:
  // 1. The backend may auto-route to different models (e.g., detecting "draw me a cat"
  //    and routing to image-generator), but this doesn't mean the user wanted to
  //    switch to that model for all future messages
  // 2. Syncing from history caused the model selector to change after backend auto-routing,
  //    making subsequent messages use the auto-routed model instead of the user's preference
  // 3. The model selector is a user preference/override, not a reflection of backend behavior
  //
  // When switching chats, the model selector resets to auto mode (empty selection).
  // The user can manually select a model if they want to force a specific one.

  // Ensure credits are calculated whenever the context changes or the model
  // selection changes. We fire even when no model is selected ("auto mode") —
  // the backend falls back to the default model (Arya) and still returns a
  // meaningful cost estimate, which keeps the credits badge populated. Without
  // this, cost stays `null` after every conversation switch / clearInput in
  // auto mode and the badge silently disappears.
  useEffect(() => {
    if (isWorkMode || isInferencing || hasPendingMessage) return;
    if (!isPlus) return;
    const contextId = currentContextId;
    if (!contextId) return;
    const hasModels = Array.isArray(inputState.selectedModelIds) && inputState.selectedModelIds.length > 0;
    const modelKey = hasModels ? inputState.selectedModelIds.join(',') : '__auto__';
    const contextRevision = `${messageCount}:${chat?.stats?.totalTokens || 0}`;
    const alreadyEnsured =
      initialCreditsEnsuredRef.current.contextId === contextId &&
      initialCreditsEnsuredRef.current.modelKey === modelKey &&
      initialCreditsEnsuredRef.current.contextRevision === contextRevision;
    if (alreadyEnsured) return;
    try {
      // Route through the debounced immediate path. Use 'setSelectedModels'
      // as the event type so it always lands in IMMEDIATE_EVENTS regardless
      // of whether a model is actually selected.
      const forcedState = { ...inputState };
      calculateCredits(forcedState, 'setSelectedModels', { selectedModelIds: inputState.selectedModelIds });
    } catch (e) {
      handleError(e, 'ensure initial credits on mount');
    }
    initialCreditsEnsuredRef.current = { contextId, modelKey, contextRevision };
  }, [currentContextId, inputState.selectedModelIds, calculateCredits, isPlus, isWorkMode,
    isInferencing, hasPendingMessage, messageCount, chat?.stats?.totalTokens]);

  // REMOVED: Effect to set initial agent selection from chat history
  //
  // The agent selector should ONLY change when the user explicitly selects an agent.
  // We do NOT sync from message history because:
  // 1. The backend may auto-route to different agents, but this doesn't mean the user
  //    wanted to switch to that agent for all future messages
  // 2. Syncing from history caused the agent selector to change after backend auto-routing
  // 3. The agent selector is a user preference/override, not a reflection of backend behavior
  //
  // When switching chats, the agent selector resets to default (no agent).
  // The user can manually select an agent if they want to use one.

  // REMOVED: Effect to set initial tools from message history
  // 
  // The search toggle should ONLY change when the user explicitly clicks it.
  // We do NOT sync from message history because:
  // 1. The backend may auto-detect and add 'search-web' to actions, but this
  //    doesn't mean the user wanted to enable the toggle
  // 2. Syncing from history caused the toggle to persist incorrectly after
  //    auto-detected searches, making subsequent requests always include search-web
  // 3. The toggle is a user preference/override, not a reflection of backend behavior
  //
  // When switching chats, the toggle resets because inputState is per-context.
  // The user can manually enable the toggle if they want explicit search.

  
  // ============================================================================
  // Render
  // ============================================================================

  const storeInputStyle = useUIStore(state => state.chatInputStyle);
  const chatInputStyle = useMemo(() => {
    return forceInputStyle || storeInputStyle;
  }, [forceInputStyle, storeInputStyle]);

  const Component = useMemo(() => {
    return chatInputStyle === CHAT_INPUT_STYLE.FLOATING ? ChatInputFloating :
           chatInputStyle === 'floating-basic' ? ChatInputFloatingBasic :
           chatInputStyle === CHAT_INPUT_STYLE.DETAILED ? ChatInputDetailed :
           ChatInputFixed;
  }, [chatInputStyle]);
  
  const composer = (
    <Component
      composerInputRef={goalCommand.inputRef}
      onCommandKeyPress={goalCommand.onKeyPress}
      goalBar={isWorkMode && !goalCommand.editing && !goalCommand.prompt ? (
        <WorkGoalBar key={currentContextId} goal={workGoal} onSend={onGoalSend} onControl={onGoalControl}
          disabled={goalControlsDisabled} />
      ) : null}
      commandSuggestions={goalCommand.editing ? (
        <WorkGoalEditor key={currentContextId} onSubmit={handleStartGoal} onCancel={goalCommand.cancelEditing}
          disabled={propIsDisabled || propIsLoading || hasUploadingFiles || hasProcessingFiles} />
      ) : (
        <GoalCommandSuggestions
          prompt={goalCommand.prompt}
          onSelect={() => goalCommand.select()}
          onDismiss={goalCommand.dismiss}
        />
      )}
      placeholder={(forcePlaceholder || isWorkMode) ? placeholder : (inputState.inputPlaceholder || placeholder)}
      value={inputState.text}
      parameters={inputState.parameters}
      files={inputState.files}
      selectedButtons={inputState.selectedButtons}
      selectedModelIds={inputState.selectedModelIds}
      selectedModels={selectedModels}
      selectedAgentIds={inputState.selectedAgentIds}
      selectedAgents={selectedAgents}
      hideAgentTags={hideAgentTags}
      showBotPicker={showBotPicker}
      selectedParameters={inputState.selectedParameters}
      banners={inputState.banners}
      quote={inputState.quote}
      isMissingRequiredParameters={missingRequiredParameters.isMissing}
      highlightedMissingParameterIds={highlightedMissingParameterIds}
      
      onSetQuote={handleOnSetQuote}
      onParameterChange={handleParameterChange}
      onSetSelectedModels={handleOnSetSelectedModels}
      onSetSelectedAgents={handleOnSetSelectedAgents}
      onActionPress={handleOnActionPress}
      onSubmit={handleOnSubmit}
      onSendNow={handleOnSendNow}
      onQueue={handleOnQueue}
      onCancel={onCancel}
      onChangeText={handleOnChangeText}
      onRemoveFile={handleOnRemoveFile}
      onToggleButton={handleOnToggleButton}
      onAddFile={handleOnAddFile}
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
      onSetFiles={handleOnSetFiles}
      onPasteFiles={handlePasteFiles}

      showGhostBanner={showGhostBanner}
      withModels={withModels && !isWorkMode}
      isWorkMode={isWorkMode}
      workFollowUpMode={workFollowUpMode}
      // Stop must stay tappable even if send would be gated (uploads, etc).
      isDisabled={performableAction === 'cancel' ? false : !isEnabled}
      // sendWhileInferencing (Work follow-up): once the box has text the
      // action slot renders Queue + Send, not the stop square alone.
      isInferencing={isInferencing && performableAction === 'cancel'}
      isLoading={(isLoading && performableAction === 'cancel') || !!propIsLoading}
      isCancelable={!!onCancel}
      autoFocus={autoFocus}
      modelInputs={modelInputs}
      modelOutputs={modelOutputs}
      conversationId={conversationId}
      collectionId={collectionId}
      hideVoiceMode={hideVoiceMode || isWorkMode}
      hideTextInput={hideTextInput && !isWorkMode}
      hasAllRequiredFiles={hasAllRequiredFiles}
      inlineParams={inlineParams}
      isCompact={isCompact}
    />
  );
  const projectBar = isWorkMode && (
    <WorkComposerProjectBar conversationId={conversationId} isDisabled={propIsDisabled || propIsLoading} />
  );
  return Platform.OS === 'web'
    ? <div ref={composerRootRef} style={{ display: 'contents' }}>{projectBar}{composer}</div>
    : <>{projectBar}{composer}</>;
}
